import React from 'react';
import type { FH110, FH112, FH114, FH117, FH081 } from '../../../services/pwh201Api';
import LookupInput from '../../../components/LookupInput';
import { fmt, dateToInput } from '../helpers';

interface Tab4Props {
  formData: Partial<FH110>;
  loanOffsets: Partial<FH112>[];
  prepaidOffsets: Partial<FH114>[];
  agentOffsets: Partial<FH117>[];
  paymentOffsets: Partial<FH081>[];
  isEditing: boolean;
  isLocked: boolean;
  setField: <K extends keyof FH110>(field: K, value: FH110[K]) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
  names: Record<string, string>;
}

const Tab4Remarks: React.FC<Tab4Props> = React.memo(({
  formData, loanOffsets, prepaidOffsets, agentOffsets, paymentOffsets,
  isEditing, isLocked, setField, resolveSingleName,
}) => {
  const disabled = !isEditing || isLocked;

  return (
    <div className="pwh201-tab-content">
      {/* Remark code + memo */}
      <div className="form-grid two-col">
        <label>備註代號</label>
        <LookupInput
          lookupType="remarkTemplate"
          contractFieldId="fh110_181"
          value={formData.fh110_181 || ''}
          onChange={v => setField('fh110_181', v)}
          onCodeConfirm={code => resolveSingleName('remarkCode', 'remarkTemplate', code)}
          disabled={disabled}
          maxLength={10}
        />
      </div>
      <div className="form-grid two-col">
        <label>備註說明</label>
        <textarea
          data-contract-field="fh110_18"
          value={formData.fh110_18 || ''}
          onChange={e => setField('fh110_18', e.target.value)}
          disabled={disabled}
          rows={3}
        />
      </div>

      {/* Paid total */}
      <div className="form-grid two-col" style={{ marginTop: 16 }}>
        <label>已付款總額</label>
        <input data-contract-field="fh110_21" value={fmt(formData.fh110_21)} readOnly disabled className="amount-field" />
      </div>

      {/* Loan Offsets (FH112) - Read-only display */}
      {loanOffsets.length > 0 && (
        <>
          <div className="pwh201-section-title" style={{ marginTop: 16 }}>沖暫借款明細</div>
          <div className="pwh201-detail-grid">
            <table>
              <thead>
                <tr>
                  <th>序號</th>
                  <th>暫借款單號</th>
                  <th>幣別</th>
                  <th>沖帳金額</th>
                </tr>
              </thead>
              <tbody>
                {loanOffsets.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.fh112_02}</td>
                    <td>{row.fh112_11}</td>
                    <td>{row.fh112_12}</td>
                    <td className="amount-cell">{fmt(row.fh112_13)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan={3}>合計</td>
                  <td className="amount-cell">
                    {fmt(loanOffsets.reduce((s, r) => s + (r.fh112_13 || 0), 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}

      {/* Prepaid Offsets (FH114) - Read-only display */}
      {prepaidOffsets.length > 0 && (
        <>
          <div className="pwh201-section-title" style={{ marginTop: 16 }}>沖預收款明細</div>
          <div className="pwh201-detail-grid">
            <table>
              <thead>
                <tr>
                  <th>序號</th>
                  <th>預收款單號</th>
                  <th>幣別</th>
                  <th>沖帳金額</th>
                </tr>
              </thead>
              <tbody>
                {prepaidOffsets.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.fh114_02}</td>
                    <td>{row.fh114_11}</td>
                    <td>{row.fh114_12}</td>
                    <td className="amount-cell">{fmt(row.fh114_13)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan={3}>合計</td>
                  <td className="amount-cell">
                    {fmt(prepaidOffsets.reduce((s, r) => s + (r.fh114_13 || 0), 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}

      {/* Agent Offsets (FH117) - Read-only display */}
      {agentOffsets.length > 0 && (
        <>
          <div className="pwh201-section-title" style={{ marginTop: 16 }}>沖代墊款明細</div>
          <div className="pwh201-detail-grid">
            <table>
              <thead>
                <tr>
                  <th>序號</th>
                  <th>代墊款單號</th>
                  <th>幣別</th>
                  <th>沖帳金額</th>
                </tr>
              </thead>
              <tbody>
                {agentOffsets.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.fh117_02}</td>
                    <td>{row.fh117_11}</td>
                    <td>{row.fh117_12}</td>
                    <td className="amount-cell">{fmt(row.fh117_13)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan={3}>合計</td>
                  <td className="amount-cell">
                    {fmt(agentOffsets.reduce((s, r) => s + (r.fh117_13 || 0), 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}

      {/* Payment Offsets (FH081) - Read-only display */}
      {paymentOffsets.length > 0 && (
        <>
          <div className="pwh201-section-title" style={{ marginTop: 16 }}>已付款沖帳明細</div>
          <div className="pwh201-detail-grid">
            <table>
              <thead>
                <tr>
                  <th>付款單號</th>
                  <th>付款金額</th>
                  <th>沖帳單號</th>
                </tr>
              </thead>
              <tbody>
                {paymentOffsets.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.fh081_03}</td>
                    <td className="amount-cell">{fmt(row.fh081_11)}</td>
                    <td>{row.fh081_12 ? dateToInput(row.fh081_12) : ''}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td>合計</td>
                  <td className="amount-cell">
                    {fmt(paymentOffsets.reduce((s, r) => s + (r.fh081_11 || 0), 0))}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
});

Tab4Remarks.displayName = 'Tab4Remarks';
export default Tab4Remarks;
